import {patch, get, post, remove} from 'utils/axios/index'
import {toast} from 'react-toastify'
import {AxiosError} from 'axios'
import {handleRejectedErrorMsg} from 'utils/helpers/handleError'
import {IFormField} from 'models/Common'
import {IProductPromotion} from 'models/ActiveManagement'
import {
 CampaignData,
 CreatePromotionResponse,
 CreatePromotionResult,
 DeleteCampaignVariantResponse,
 fetchCampaignResponse,
 IUpdateCampaignPayload,
 LanguageItem,
 LanguageListResponse,
 UpdateCampaignResponse,
} from 'models/CreatePromotion'
import {DefaultApiResponse} from 'models/ApiResponse'
import {DEFAULT_TEMPLATE_VARIABLES} from 'constants/common'

const fetchProductPromotions = async (
 page: number,
 pageSize: number,
 query?: IFormField,
 signal?: AbortSignal
): Promise<{data: IProductPromotion[]; total: number}> => {
 try {
  const params: IFormField = {
   page,
   page_size: pageSize,
   ...query,
  }
  const res = await get<DefaultApiResponse>(
   `/marketing/campaign/list`,
   params,
   {signal}
  )
  if (res?.success) {
   return {
    data: res.data?.results,
    total: res.data?.count,
   }
  }
  toast.error('Something went wrong')
  return {data: [], total: 0}
 } catch (err) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)

  return {data: [], total: 0}
 }
}

const createPromotionApi = async (
 payload: IUpdateCampaignPayload
): Promise<CreatePromotionResult> => {
 try {
  const finalPayload = {
   ...payload,
   template_variables: DEFAULT_TEMPLATE_VARIABLES,
  }
  const res = await post<CreatePromotionResponse>(
   `/marketing/campaign/create`,
   finalPayload
  )
  if (res?.success) {
   toast.success(
    res?.message || 'Promotion created successfully'
   )
   return {
    success: true,
    data: res.data,
   }
  }
  toast.error('Something went wrong')
  return {
   success: false,
   data: null,
  }
 } catch (err) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return {
   success: false,
   data: null,
  }
 }
}

const fetchCampaignDetail = async (
 id: number | string
): Promise<{data: CampaignData | null}> => {
 try {
  const res = await get<fetchCampaignResponse>(
   `/marketing/campaign/details?campaign_id=${id}`
  )
  if (res?.data) {
   return {data: res.data}
  }
  toast.error('Something went wrong')
  return {data: null}
 } catch (err) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return {data: null}
 }
}

const updateCampaignApi = async (
 payload: IUpdateCampaignPayload
): Promise<{data: string | null}> => {
 try {
  const res = await patch<UpdateCampaignResponse>(
   `/marketing/campaign/update`,
   payload
  )

  if (res?.success) {
   toast.success(
    res.message || 'Promotion updated successfully'
   )
   return {data: res.message}
  }

  toast.error('Something went wrong')
  return {data: null}
 } catch (err) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return {data: null}
 }
}
const deleteCampaignVariantApi = async (
 campaign_variant_id: number
): Promise<{success: boolean}> => {
 try {
  const res = await remove<DeleteCampaignVariantResponse>(
   `/marketing/campaign-variant/delete?campaign_variant_id=${campaign_variant_id}`,
   {}
  )

  if (res.success) {
   toast.success(
    res.message || 'Campaign variant deleted successfully'
   )
   return {success: true}
  }

  toast.error(res.message || 'Something went wrong')
  return {success: false}
 } catch (err) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return {success: false}
 }
}

const fetchLanguageList = async (): Promise<
 LanguageItem[]
> => {
 try {
  const res = await get<LanguageListResponse>(
   `common/language-list`
  )

  if (res.success) {
   return res.data
  }

  return []
 } catch (error) {
  console.error('Failed to fetch language list', error)
  return []
 }
}

export {
 fetchProductPromotions,
 createPromotionApi,
 fetchCampaignDetail,
 updateCampaignApi,
 deleteCampaignVariantApi,
 fetchLanguageList,
}
